import apiClient from './client';
import { User, ApiResponse } from '@/types';

/**
 * LDAP Group info
 */
export interface LdapGroup {
  dn: string;
  name: string;
  description?: string;
}

/**
 * LDAP User/Manager info
 */
export interface LdapManager {
  dn: string;
  username: string;
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
}

/**
 * User management details response
 */
export interface UserManagementDetails {
  user: User;
  ldapGroups: LdapGroup[];
  manager: LdapManager | null;
}

/**
 * Password reset result
 */
export interface PasswordResetResult {
  success: boolean;
  emailSent: boolean;
  error?: string;
}

/**
 * Get all available LDAP groups
 */
export async function getLdapGroups(): Promise<LdapGroup[]> {
  const response = await apiClient.get<ApiResponse<LdapGroup[]>>('/user-management/groups');
  return response.data.data as LdapGroup[];
}

/**
 * Get user management details (groups, manager)
 */
export async function getUserManagementDetails(userId: string): Promise<UserManagementDetails> {
  const response = await apiClient.get<ApiResponse<UserManagementDetails>>(`/user-management/${userId}`);
  return response.data.data as UserManagementDetails;
}

/**
 * Deactivate user in LDAP and MongoDB
 */
export async function deactivateUser(userId: string): Promise<{ message: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>(`/user-management/${userId}/deactivate`);
  return response.data.data as { message: string };
}

/**
 * Activate user in LDAP and MongoDB
 */
export async function activateUser(userId: string): Promise<{ message: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>(`/user-management/${userId}/activate`);
  return response.data.data as { message: string };
}

/**
 * Change user's LDAP password
 */
export async function changePassword(userId: string, newPassword: string): Promise<{ message: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>(
    `/user-management/${userId}/change-password`,
    { newPassword }
  );
  return response.data.data as { message: string };
}

/**
 * Change the user's email in LDAP and the app (CEO)
 */
export async function updateEmail(userId: string, email: string): Promise<User> {
  const response = await apiClient.put<ApiResponse<User>>(`/user-management/${userId}/email`, { email });
  return response.data.data as User;
}

/**
 * Reset user password and send via email
 */
export async function resetPassword(userId: string): Promise<PasswordResetResult> {
  const response = await apiClient.post<ApiResponse<PasswordResetResult>>(
    `/user-management/${userId}/reset-password`
  );
  return response.data.data as PasswordResetResult;
}

/**
 * Add user to LDAP group
 */
export async function addUserToGroup(userId: string, groupDn: string): Promise<{ message: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>(
    `/user-management/${userId}/groups/add`,
    { groupDn }
  );
  return response.data.data as { message: string };
}

/**
 * Remove user from LDAP group
 */
export async function removeUserFromGroup(userId: string, groupDn: string): Promise<{ message: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string }>>(
    `/user-management/${userId}/groups/remove`,
    { groupDn }
  );
  return response.data.data as { message: string };
}
